import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, onSnapshot, addDoc,
  doc, getDoc, updateDoc, serverTimestamp
} from '../../firebase/config';
import {
  Send, User, ChevronLeft, MoreVertical,
  MessageCircle, Check, CheckCheck, ExternalLink
} from 'lucide-react';

const ProfessionalChatPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user, userData } = useAuth();

  const [client, setClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2200);
  };

  // ---------- Load client info ----------
  useEffect(() => {
    if (!clientId) return;

    const loadClient = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', clientId));
        if (snap.exists()) {
          setClient({ id: snap.id, ...snap.data() });
        } else {
          setClient({ id: clientId, name: 'Client' });
        }
      } catch (err) {
        console.error('Failed to load client:', err);
        setClient({ id: clientId, name: 'Client' });
      }
    };

    loadClient();
  }, [clientId]);

  // ---------- Stream messages with this client ----------
  useEffect(() => {
    if (!user?.uid || !clientId) return;

    const q = query(
      collection(db, 'messages'),
      where('professionalId', '==', user.uid),
      where('clientId', '==', clientId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      msgs.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return aT - bT; // oldest first
      });
      setMessages(msgs);
      setLoading(false);

      // Mark incoming client messages as read
      msgs.forEach(m => {
        if (m.sender === 'client' && !m.read) {
          updateDoc(doc(db, 'messages', m.id), { read: true }).catch(() => {});
        }
      });
    }, (err) => {
      console.error('Chat stream error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid, clientId]);

  // ---------- Auto-scroll on new messages ----------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ---------- Send message ----------
  const sendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !clientId || sending) return;

    setSending(true);
    try {
      const ids = [user.uid, clientId].sort();
      const conversationId = `${ids[0]}_${ids[1]}`;

      await addDoc(collection(db, 'messages'), {
        conversationId,
        clientId,
        clientName: client?.name || client?.businessName || 'Client',
        clientImage: client?.profileImage || client?.photoURL || null,
        professionalId: user.uid,
        professionalName: userData?.businessName || userData?.name || 'Professional',
        professionalImage: userData?.profileImage || userData?.photoURL || null,
        sender: 'professional',
        text,
        read: false,
        createdAt: serverTimestamp(),
      });

      setNewMessage('');
      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to send message:', err);
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ---------- Format helpers ----------
  const formatTime = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  const sameDay = (a, b) => {
    if (!a || !b) return false;
    const da = a.toDate ? a.toDate() : new Date(a);
    const db_ = b.toDate ? b.toDate() : new Date(b);
    return (
      da.getFullYear() === db_.getFullYear() &&
      da.getMonth() === db_.getMonth() &&
      da.getDate() === db_.getDate()
    );
  };

  const formatDayLabel = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((today - target) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-ZA', { weekday: 'long' });
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-20 flex flex-col h-[calc(100vh-120px)] lg:h-[calc(100vh-160px)]">

      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="bg-white rounded-t-3xl border border-gray-100 p-4 flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate('/professional-dashboard/inbox')}
          className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] hover:bg-[#E4D9F7] transition-colors shrink-0"
        >
          <ChevronLeft size={20} />
        </button>

        <div className="w-11 h-11 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0 overflow-hidden">
          {client?.profileImage || client?.photoURL ? (
            <img
              src={client.profileImage || client.photoURL}
              alt={client?.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <User size={20} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-gray-900 text-sm truncate">
            {client?.name || client?.businessName || 'Client'}
          </h2>
          <p className="text-[11px] text-gray-400 truncate">
            {client?.email || 'Client'}
          </p>
        </div>

        <button
          onClick={() => client?.id && navigate(`/professional/${client.id}`)}
          className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] hover:bg-[#E4D9F7] transition-colors shrink-0"
          title="View client profile"
        >
          <ExternalLink size={16} />
        </button>
      </div>

      {/* ============================================ */}
      {/* MESSAGES */}
      {/* ============================================ */}
      <div className="flex-1 overflow-y-auto bg-[#FCF8F9] border-x border-gray-100 px-4 py-6 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle size={32} className="text-[#AF8BD4]" />
            </div>
            <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
              Start the conversation
            </h3>
            <p className="text-gray-400 text-sm">
              Send a message to {client?.name?.split(' ')[0] || 'your client'}
            </p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const mine = msg.sender === 'professional';
            const prevMsg = messages[idx - 1];
            const showDate = !prevMsg || !sameDay(prevMsg.createdAt, msg.createdAt);

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest py-4">
                    {formatDayLabel(msg.createdAt)}
                  </div>
                )}
                <div className={`flex ${mine ? 'justify-end' : 'justify-start'} mb-3`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                      mine
                        ? 'bg-[#010101] text-white rounded-br-md'
                        : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">
                      {msg.text}
                    </p>
                    <div className={`flex items-center gap-1 mt-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                      <span className={`text-[10px] ${mine ? 'text-white/60' : 'text-gray-400'}`}>
                        {formatTime(msg.createdAt)}
                      </span>
                      {mine && (
                        msg.read
                          ? <CheckCheck size={10} className="text-white/60" />
                          : <Check size={10} className="text-white/60" />
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ============================================ */}
      {/* INPUT */}
      {/* ============================================ */}
      <div className="bg-white rounded-b-3xl border border-gray-100 border-t-0 p-4 shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 bg-[#FCF8F9] border border-gray-100 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] resize-none max-h-32"
            style={{ minHeight: '44px' }}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="w-11 h-11 bg-[#010101] text-white rounded-2xl hover:bg-black/90 transition-colors disabled:opacity-40 flex items-center justify-center shrink-0"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* TOAST */}
      {/* ============================================ */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalChatPage;